import { createFileLogger } from '../../../../../utils/logger';
import { DocEntity } from '../../../types/docs';
import { entityService } from '../../../../../services/entityService';
import fetchService from '../../../../../services/fetchService';
import { API_BASE_URL } from '../../../../../api/config';

const log = createFileLogger('DOCS_SUMMARY_SERVICE');

interface SummaryConfig {
  minCharsForSummary: number;
  maxSummaryLength: number;
  debounceMs: number;
  changeThresholdChars: number;
  changeThresholdPercent: number;
}

const DEFAULT_CONFIG: SummaryConfig = {
  minCharsForSummary: 500, // Only summarize docs with 500+ chars
  maxSummaryLength: 300,   // Keep summaries under 300 chars
  debounceMs: 10000,       // Wait 10 seconds after last change
  changeThresholdChars: 100, // Require 100+ char change
  changeThresholdPercent: 10 // Or 10%+ change
};

interface DocumentAnalysisResponse {
  documentType: string;
  summary: string;
}

class SummaryService {
  private config: SummaryConfig = DEFAULT_CONFIG;
  private pendingSummaries = new Map<string, NodeJS.Timeout>();

  // Fast hash function for content change detection
  private simpleHash(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  // Check if content has changed significantly enough to warrant re-summarization
  private shouldUpdateSummary(newContent: string, currentSummary?: DocEntity['skeleton']['summary']): boolean {
    if (!currentSummary) return true;
    if (newContent.length < this.config.minCharsForSummary) return false;

    const newHash = this.simpleHash(newContent);
    if (newHash === currentSummary.contentHash) return false;

    const charDifference = Math.abs(newContent.length - currentSummary.charCount);
    const percentChange = (charDifference / currentSummary.charCount) * 100;

    return charDifference >= this.config.changeThresholdChars || 
           percentChange >= this.config.changeThresholdPercent;
  }

  // Generate summary and document type using their existing AI infrastructure
  private async generateDocumentAnalysis(content: string): Promise<DocumentAnalysisResponse> {
    try {
      const prompt = `Please analyze this document and provide a JSON response with exactly this structure:

{
  "documentType": "Brief category (e.g., 'Scientific Paper', 'Meeting Notes', 'Product Requirements', 'Technical Documentation', 'Creative Writing', 'Research Notes', 'Business Plan', etc.)",
  "summary": "Concise summary in under ${this.config.maxSummaryLength} characters"
}

Document content:
${content.slice(0, 4000)}${content.length > 4000 ? '\n\n[Content truncated for analysis...]' : ''}

Respond ONLY with the JSON object, no other text.`;

      // Use Claude chat endpoint (FastAPI compatible)
      const response = await fetchService(`${API_BASE_URL}/claude/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          query: prompt,
          model: 'claude-opus-4-1-20250805',
          context: { 
            task: 'document_analysis',
            maxSummaryLength: this.config.maxSummaryLength 
          },
          conversation_history: []
        })
      });

      if (!response.response) {
        throw new Error('No response from AI service');
      }

      // Parse the JSON response from the AI
      let analysisResult: DocumentAnalysisResponse;
      try {
        // Extract JSON from response (in case there's extra text)
        const jsonMatch = response.response.match(/\{[\s\S]*\}/);
        const jsonStr = jsonMatch ? jsonMatch[0] : response.response;
        analysisResult = JSON.parse(jsonStr);
      } catch (parseError) {
        // Fallback: create a basic summary from the response
        analysisResult = {
          documentType: 'Document',
          summary: response.response.slice(0, this.config.maxSummaryLength)
        };
      }

      // Validate and sanitize the response
      if (!analysisResult.documentType || !analysisResult.summary) {
        throw new Error('Invalid analysis response structure');
      }

      // Ensure summary length is within limits
      if (analysisResult.summary.length > this.config.maxSummaryLength) {
        analysisResult.summary = analysisResult.summary.slice(0, this.config.maxSummaryLength - 3) + '...';
      }

      return analysisResult;
    } catch (error) {
      // Ultimate fallback: generate a basic analysis locally
      return this.generateFallbackAnalysis(content);
    }
  }

  // Local fallback analysis when AI services are unavailable
  private generateFallbackAnalysis(content: string): DocumentAnalysisResponse {
    const words = content.split(/\s+/).filter(word => word.length > 0);
    const wordCount = words.length;
    
    // Basic document type detection based on keywords
    let documentType = 'Document';
    const lowerContent = content.toLowerCase();
    
    if (lowerContent.includes('meeting') || lowerContent.includes('agenda') || lowerContent.includes('minutes')) {
      documentType = 'Meeting Notes';
    } else if (lowerContent.includes('requirements') || lowerContent.includes('specification')) {
      documentType = 'Requirements Document';
    } else if (lowerContent.includes('research') || lowerContent.includes('abstract') || lowerContent.includes('conclusion')) {
      documentType = 'Research Document';
    } else if (lowerContent.includes('todo') || lowerContent.includes('task') || lowerContent.includes('deadline')) {
      documentType = 'Task List';
    } else if (lowerContent.includes('proposal') || lowerContent.includes('plan') || lowerContent.includes('strategy')) {
      documentType = 'Business Plan';
    } else if (wordCount < 200) {
      documentType = 'Notes';
    } else if (wordCount > 1000) {
      documentType = 'Long Document';
    }
    
    // Generate a simple summary from the first few sentences
    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 10);
    const firstSentences = sentences.slice(0, 3).join('. ').trim();
    let summary = firstSentences || content.slice(0, 200);
    
    // Truncate to max length
    if (summary.length > this.config.maxSummaryLength) {
      summary = summary.slice(0, this.config.maxSummaryLength - 3) + '...';
    }
    
    return { documentType, summary };
  }

  // Public method to schedule summary update
  public scheduleSummaryUpdate(docId: string, content: string, currentSummary?: DocEntity['skeleton']['summary']): void {
    // TEMPORARILY DISABLED: Document analysis causing API errors
    log.log(`[SummaryService] Document analysis temporarily disabled for doc ${docId.slice(-8)}`);
    return;
    
    // Clear any pending summary for this doc
    const existingTimeout = this.pendingSummaries.get(docId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Check if update is needed
    if (!this.shouldUpdateSummary(content, currentSummary)) {
      return;
    }

    // Schedule new summary generation
    const timeout = setTimeout(async () => {
      try {
        await this.updateSummary(docId, content);
        this.pendingSummaries.delete(docId);
      } catch (error) {
        this.pendingSummaries.delete(docId);
      }
    }, this.config.debounceMs);

    this.pendingSummaries.set(docId, timeout);
  }

  // Force immediate summary update
  public async updateSummary(docId: string, content: string): Promise<void> {
    if (content.length < this.config.minCharsForSummary) {
      return;
    }

    try {
      const analysis = await this.generateDocumentAnalysis(content);
      
      const summary = {
        text: analysis.summary,
        documentType: analysis.documentType,
        contentHash: this.simpleHash(content),
        generatedAt: new Date().toISOString(),
        wordCount: content.split(/\s+/).filter(word => word.length > 0).length,
        charCount: content.length,
        model: 'claude-opus-4-1-20250805',
        version: 1
      };

      await entityService.mergeUpdate(docId, {
        skeleton: { summary }
      });

    } catch (error) {
      throw error;
    }
  }

  // Cancel pending summary for a document
  public cancelPendingSummary(docId: string): void {
    const timeout = this.pendingSummaries.get(docId);
    if (timeout) {
      clearTimeout(timeout);
      this.pendingSummaries.delete(docId);
    }
  }

  // Get summary status
  public getPendingStatus(docId: string): boolean {
    return this.pendingSummaries.has(docId);
  }

  // Get config for debugging/tuning
  public getConfig(): SummaryConfig {
    return { ...this.config };
  }

  // Update config
  public updateConfig(newConfig: Partial<SummaryConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

export const summaryService = new SummaryService();